/**
 * KpiTile — one number, said plainly, with its provenance attached.
 *
 * The freshness chip is built in rather than left to the caller, because a live
 * number without one is the failure this platform treats as cardinal. A tile
 * fed by live data passes `lastUpdatedAt`; a tile whose source is not connected
 * passes `notConnectedLabel` and shows that instead of a plausible-looking zero.
 *
 * The tile is a figure, not a control, unless the caller gives it an action.
 */
import React from 'react';
import { cn } from '../lib/utils';
import { FreshnessIndicator, type FreshnessState } from './FreshnessIndicator';

export type KpiTone = 'default' | 'good' | 'warning' | 'critical';

export interface KpiTileProps {
  label: string;
  /** The headline. Pass a string when the value is not a number. */
  value: React.ReactNode;
  /** One line under the value. */
  detail?: React.ReactNode;
  tone?: KpiTone;
  /** Chart or meter rendered under the value — a Sparkline, usually. */
  children?: React.ReactNode;

  /** Live-data provenance. Omit all three on a tile that is not live. */
  lastUpdatedAt?: string | null;
  expectedRefreshMs?: number;
  staleMs?: number;
  /** Force the chip, e.g. 'offline' while a realtime channel is not subscribed. */
  freshnessState?: FreshnessState;
  freshnessLabel?: string;

  /**
   * Shown in place of the value when the source is not connected. The platform
   * rule is to say so rather than render a placeholder number.
   */
  notConnectedLabel?: string;
  isConnected?: boolean;

  onClick?: () => void;
  className?: string;
}

const TONE_VALUE: Record<KpiTone, string> = {
  default: 'text-text',
  good: 'text-success',
  warning: 'text-warning',
  critical: 'text-notification',
};

export function KpiTile({
  label,
  value,
  detail,
  tone = 'default',
  children,
  lastUpdatedAt,
  expectedRefreshMs,
  staleMs,
  freshnessState,
  freshnessLabel,
  notConnectedLabel,
  isConnected = true,
  onClick,
  className,
}: KpiTileProps) {
  const showsFreshness = !!(lastUpdatedAt || freshnessState);
  const disconnected = !isConnected && !!notConnectedLabel;

  const body = (
    <>
      <p className="text-sm text-text-muted">{label}</p>
      {disconnected ? (
        <p className="mt-1 text-lg font-medium leading-snug text-text-muted">{notConnectedLabel}</p>
      ) : (
        <p className={cn('mt-1 text-xl font-semibold leading-tight sm:text-2xl', TONE_VALUE[tone])}>
          {value}
        </p>
      )}
      {detail && <p className="mt-1 text-xs text-text-faint">{detail}</p>}
      {children && <div className="mt-3">{children}</div>}
    </>
  );

  return (
    <div className={cn('rounded-lg border border-border bg-surface p-4 sm:p-5', className)}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="w-full rounded-sm text-left hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {body}
        </button>
      ) : (
        body
      )}
      {/* Outside the control: the chip is a live region, and a live region
          inside a button is announced as part of the button. */}
      {showsFreshness && !disconnected && (
        <FreshnessIndicator
          className="mt-3"
          lastUpdatedAt={lastUpdatedAt}
          forcedState={freshnessState}
          expectedRefreshMs={expectedRefreshMs}
          staleMs={staleMs}
          label={freshnessLabel ?? label}
        />
      )}
    </div>
  );
}
